/* eslint-disable max-lines -- to be fixed when splitting objects/objectless code */
import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { every, size } from 'lodash';
import {
  isPointCloudOutput,
  isVideoOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import { getOutputHelper } from '../../../shared/output-helpers/get-output-helper';
import { ContextMenuOption } from '../../../team-apis/content/content-connector';
import { VisibilityState } from '../../../team-apis/otterdam/answers/shape-typings';
import { getOutputByName } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { outputInfoStoreToken } from '../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import { RootFocusedShapeService } from '../../outputs/annotation/annotation-shared/utils/root-focused-shape.service';
import { RootShapeSelectionService } from '../../outputs/annotation/annotation-shared/utils/root-shape-selection.service';
import { SelectionService } from '../../outputs/annotation/annotation-shared/utils/selection.service';
import { VectorToPointCloudShapeService } from '../../outputs/annotation/annotation-shared/utils/vector-to-point-cloud-shape.service';
import { FeedbackListService } from '../feedback/feedback-list.service';
import { ShapeFeedbackDialogService } from '../feedback/shape-feedback-dialog.service';
import { AnnotationCreateDeleteService } from '../misc-services/annotation-create-delete.service';
import { FeatureFlagService } from '../misc-services/feature-flag.service';
import { PointCloudToVectorShapeService } from '../misc-services/point-cloud-to-vector-shape.service';
import { ShapeVisibilityService } from '../misc-services/shape-visibility.service';
import { SubpointBindingService } from '../misc-services/subpoint-binding.service';
import { AnnotationCopyService } from '../shape-copy-and-paste/annotation-copy.service';
import { AnnotationPastingService } from '../shape-copy-and-paste/annotation-pasting.service';
import { PasteValidationService } from '../shape-copy-and-paste/paste-validation.service';
import {
  isAtMostOneLutAvailable,
  isUnprojectionEnabled,
} from '../utils/projection-utils';

@Injectable()
export class ContextMenuOptionsService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private outputInfoStore = inject(outputInfoStoreToken);
  private output = this.outputInfoStore.state().output;
  private featureFlagService = inject(FeatureFlagService);
  private focusedShapeService = inject(RootFocusedShapeService);
  private rootShapeSelectionService = inject(RootShapeSelectionService);
  private fusionManager = inject(FusionManager);
  private annotationCopyService = inject(AnnotationCopyService);
  private annotationPastingService = inject(AnnotationPastingService);
  private shapeFeedbackDialogService = inject(ShapeFeedbackDialogService);
  private subpointBindingService = inject(SubpointBindingService);
  private pasteValidationService = inject(PasteValidationService);
  private shapeVisibilityService = inject(ShapeVisibilityService);
  private annotationCreateDeleteService = inject(AnnotationCreateDeleteService);
  private selectionService = inject(SelectionService);
  private vectorToPointCloudShapeService = inject(
    VectorToPointCloudShapeService,
  );
  private pointCloudToVectorShapeService = inject(
    PointCloudToVectorShapeService,
  );
  private feedbackListService = inject(FeedbackListService);

  async getCanvasContextMenuOptions() {
    return [await this.getPasteContextMenuOption()];
  }

  async getShapeContextMenuOptions() {
    const menuOptions: ContextMenuOption[] = [
      ...this.getCopyShapesMenuOptions(),
      await this.getPasteContextMenuOption(),
    ];

    menuOptions.push(this.getAddFeedbackMenuOption());
    if (this.featureFlagService.hasFlag('subpoint-tags')) {
      menuOptions.push(
        this.subpointBindingService.getGeneratePointsContextMenuOptions(),
      );
    }
    const focusedShapeIndex = this.focusedShapeService.getFocusedShapeIndex(
      this.output.name,
    );

    if (isDefined(focusedShapeIndex)) {
      const projectionOptions =
        await this.getShapeProjectionOptions(focusedShapeIndex);
      menuOptions.push(...projectionOptions);
    }

    return menuOptions;
  }

  getShapeListContextMenuOptions(_targetedShapeIndex: number) {
    return [
      ...this.getVisibilityContextMenuOptions(),
      this.getAddFeedbackMenuOption(),
      this.getDeleteShapesContextMenuOption(),
    ];
  }

  getTimelineContextMenuOptions() {
    return [this.getAddFeedbackMenuOption()];
  }

  private async getShapeProjectionOptions(focusedShapeIndex: number) {
    const options = [];
    if (this.shouldAllowProjectCuboids(focusedShapeIndex)) {
      options.push(await this.getProjectCuboid3DOption(focusedShapeIndex));
    }

    if (this.shouldAllowProjectRectangles(focusedShapeIndex)) {
      options.push(this.getProjectRectanglesOption(focusedShapeIndex));
    }
    return options;
  }

  private getDeleteShapesContextMenuOption() {
    return {
      label: 'Delete',
      hidden: this.taskWorkspaceStore.state().readOnly,
      action: () => {
        const selectedShapeIndexes =
          this.selectionService.getSelectedShapeIndexes();
        this.annotationCreateDeleteService.deleteShapes(selectedShapeIndexes, {
          source: 'context_menu',
        });
      },
    };
  }

  private getVisibilityContextMenuOptions() {
    const getVisibilityAction = (newVisibility: VisibilityState) => () =>
      this.shapeVisibilityService.changeShapeVisibility(
        { source: 'context_menu' },
        this.selectionService.getSelectedShapeIndexes(),
        newVisibility,
      );

    return [
      {
        label: 'Visible',
        action: getVisibilityAction(1),
        hidden: this.taskWorkspaceStore.state().readOnly,
      },
      {
        label: 'Invisible',
        action: getVisibilityAction(0),
        hidden: this.taskWorkspaceStore.state().readOnly,
      },
    ];
  }

  private getAddFeedbackMenuOption() {
    return {
      label: 'Add feedback',
      hidden: this.shouldHideAddFeedback(),
      disabled: this.shouldDisableAddFeedback(),
      action: () => {
        this.feedbackListService.switchOutput(this.getFocusedShapeOutputId());
        this.shapeFeedbackDialogService.openAddShapeFeedbackDialog();
      },
    };
  }

  private async getPasteContextMenuOption() {
    const eventTrackingValue = { source: 'context_menu' };
    return {
      label: 'Paste',
      action: () =>
        this.annotationPastingService.pasteAnnotations(eventTrackingValue),
      shortcut: 'pasteShapes',
      disabled: await this.pasteValidationService.isPasteInvalid(),
      hidden: this.taskWorkspaceStore.state().readOnly,
    };
  }

  private getCopyShapesMenuOptions() {
    const { output } = this.outputInfoStore.state();
    const { isMultiFrameOutput } = getOutputHelper(output.type);

    let copyShapeLabel = 'Copy';
    if (isMultiFrameOutput) {
      copyShapeLabel = 'Copy shapes (current frame)';
    }
    const eventTrackingValue = { source: 'context_menu' };

    const options = [
      {
        label: copyShapeLabel,
        action: () =>
          this.annotationCopyService.copyAnnotations(eventTrackingValue, true),
        shortcut: 'copySelectedShapes',
      },
    ];

    if (isMultiFrameOutput) {
      options.push(...this.buildCopyShapesAllFramesOption());
    }

    return options;
  }

  private async getProjectCuboid3DOption(shapeId: number) {
    const { lutData, readOnly } = this.taskWorkspaceStore.state();
    const shouldShowOption =
      !readOnly && isAtMostOneLutAvailable(this.taskWorkspaceStore.state());

    const option: ContextMenuOption = {
      label: 'Create 3D cuboid',
      action: () =>
        this.vectorToPointCloudShapeService.upsertProjectedCuboid(shapeId),
      shortcut: '',
      disabled: !isUnprojectionEnabled(
        this.taskWorkspaceStore.state(),
        this.output.name,
        lutData,
      ),
      hidden: !shouldShowOption,
    };
    return option;
  }

  private getProjectRectanglesOption(shapeId: number) {
    const { readOnly } = this.taskWorkspaceStore.state();
    const eventTrackingValue = { method: '3d_context_menu' };

    const option: ContextMenuOption = {
      label: 'Generate 2D Rectangle(s)',
      action: () =>
        this.pointCloudToVectorShapeService.createProjectedRectangles(
          shapeId,
          eventTrackingValue,
        ),
      disabled: !this.areAllVideosLoadedAtCurrentFrame(),
      hidden: readOnly,
    };
    return option;
  }

  private shouldHideAddFeedback() {
    return !this.shapeFeedbackDialogService.isEditable();
  }

  private shouldAllowProjectCuboids(shapeIndex: number) {
    const { type } = this.taskWorkspaceStore
      .getShapeStore(this.output.name, shapeIndex)
      .state();
    return (
      isVideoOutput(this.output) &&
      this.fusionManager.getPrimaryMinion().output.type ===
        'PointCloudOutput' &&
      (type === 'rectangle' || type === 'slider_rectangle')
    );
  }

  private shouldAllowProjectRectangles(shapeIndex: number) {
    const { type } = this.taskWorkspaceStore
      .getShapeStore(this.output.name, shapeIndex)
      .state();
    const { outputs } = this.taskWorkspaceStore.state().project;

    return (
      isPointCloudOutput(this.outputInfoStore.state().output) &&
      type === 'cuboid' &&
      outputs.some(
        (output) =>
          isVideoOutput(output) &&
          !output.meta.is_orthographic_projection &&
          output.meta.available_tools.includes('Rectangle'),
      )
    );
  }

  private shouldDisableAddFeedback() {
    return size(this.rootShapeSelectionService.getNestedSelection()) !== 1;
  }

  private buildCopyShapesAllFramesOption() {
    const eventTrackingValue = { source: 'context_menu' };
    return [
      {
        label: 'Copy shapes (all frames)',
        action: () =>
          this.annotationCopyService.copyAnnotations(eventTrackingValue, false),
        shortcut: 'copySelectedShapesAllFrames',
      },
    ];
  }

  private areAllVideosLoadedAtCurrentFrame() {
    const { outputAnnotationInfo } = this.taskWorkspaceStore.state();
    const videoOutputs =
      this.pointCloudToVectorShapeService.getProjectableOutputs();

    const currentFrame = this.fusionManager.getFrameNumberForOutput(
      this.outputInfoStore.state().output.name,
    );

    return every(
      videoOutputs.map((output) => {
        const sensorRendererState =
          outputAnnotationInfo[output.name]?.sensorRendererState;

        // assuming all videos have the same amount of frames
        return sensorRendererState?.loadedFrames[currentFrame];
      }),
    );
  }

  private getFocusedShapeOutputId() {
    const focusedAnnotationStore =
      this.focusedShapeService.getFocusedShapeStore();
    assert(focusedAnnotationStore, 'No focused shape');
    return getOutputByName(
      this.taskWorkspaceStore.state().project,
      focusedAnnotationStore.state().outputName!,
    ).id;
  }
}
